import {gql} from '@apollo/client';
import React from 'react';

import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import notifications from 'notifications';
import {
  handleSelectRequestV2Definition,
  CREATE_REQUEST_V2_SUBMISSION_SHORTCUT_BUTTON_FRAGMENT,
} from 'processes/requests_v2/shortcuts/CreateRequestV2SubmissionShortcutButton';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Menu from 'ui-library/menus/Menu';
import {OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {withLoadingScreen} from 'util/Promises';

import {GetRequestV2SubmissionForCreateDropdownQuery} from './__generated/GetRequestV2SubmissionForCreateDropdownQuery';
import {
  GetRequestV2SubmissionForCreateOptionQuery,
  GetRequestV2SubmissionForCreateOptionQueryVariables,
} from './__generated/GetRequestV2SubmissionForCreateOptionQuery';
import getProcessTaskColumnMappingInfoFromQuery from './getProcessTaskColumnMappingInfoFromQuery';

type RequestV2DefinitionForCreateDropdown = ExtractByTypename<
  GetRequestV2SubmissionForCreateDropdownQuery['allRequestV2Definitions'][number],
  'RequestV2DefinitionNode'
>;

export const GET_REQUEST_V2_SUBMISSION_FOR_CREATE_DROPDOWN_QUERY = gql`
  query GetRequestV2SubmissionForCreateDropdownQuery @skipAudit @engComponent(name: WORKFLOWS) {
    allRequestV2Definitions {
      id
      name
      archiveData {
        archived
        purpose
      }
    }
  }
`;

export function useRequestV2DefinitionsForCreateDropdownWithLoading(): Array<RequestV2DefinitionForCreateDropdown> | null {
  const result = useBenchlingQuery<GetRequestV2SubmissionForCreateDropdownQuery>(
    GET_REQUEST_V2_SUBMISSION_FOR_CREATE_DROPDOWN_QUERY
  );
  if (result.type === QueryResultType.SUCCESS) {
    return result.data.allRequestV2Definitions.filter(
      (requestV2Definition) => !isArchived(requestV2Definition)
    );
  }
  return null;
}

export const GET_REQUEST_V2_SUBMISSION_FOR_CREATE_OPTION_QUERY = gql`
  query GetRequestV2SubmissionForCreateOptionQuery($id: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    requestV2Definition(id: $id) {
      ... on RequestV2DefinitionNode {
        ...CreateRequestV2SubmissionShortcutButtonFragment
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${CREATE_REQUEST_V2_SUBMISSION_SHORTCUT_BUTTON_FRAGMENT}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getOptionsForCreate(
  requestV2Definitions: Array<RequestV2DefinitionForCreateDropdown>,
  runQueryState: RunQueryState,
  selectorsById: SchemaFieldSelectorsDict
): JSX.Element {
  const sourceColumns = getProcessTaskColumnMappingInfoFromQuery(runQueryState);
  return (
    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
      <Section>
        <SectionHeader>Request templates</SectionHeader>
        {requestV2Definitions.length ? (
          requestV2Definitions.map((requestV2Definition) => (
            <Option
              key={requestV2Definition.id}
              contentType={OptionContentType.PLAIN}
              onSelect={async () => {
                const result = await withLoadingScreen(
                  doQuerySafe<
                    GetRequestV2SubmissionForCreateOptionQuery,
                    GetRequestV2SubmissionForCreateOptionQueryVariables
                  >(
                    {query: GET_REQUEST_V2_SUBMISSION_FOR_CREATE_OPTION_QUERY},
                    {
                      variables: {id: requestV2Definition.id},
                    }
                  )
                );

                if (isInaccessible(result.requestV2Definition)) {
                  notifications.error('Selected request template is not accessible');
                  return;
                }

                await handleSelectRequestV2Definition(
                  sourceColumns,
                  result.requestV2Definition,
                  selectorsById,
                  ActionOrigin.INSIGHTS
                );
              }}
              shouldEllipsize={false}
              value={requestV2Definition.name}
            >
              {requestV2Definition.name}
            </Option>
          ))
        ) : (
          <Option contentType={OptionContentType.PLAIN} isDisabled={true}>
            No request templates found
          </Option>
        )}
      </Section>
    </Menu>
  );
}

export default function getCreateRequestV2SubmissionOption(props: {
  runQueryState: RunQueryState;
  requestV2Definitions: Array<RequestV2DefinitionForCreateDropdown> | null;
  selectorsById: SchemaFieldSelectorsDict;
}): JSX.Element {
  return (
    <Option
      icon={ModelTypeConstants.REQUEST_V2_SUBMISSION}
      isDisabled={props.runQueryState.status !== AsyncStatus.SUCCESS}
    >
      Create request
      {props.runQueryState.status === AsyncStatus.SUCCESS &&
        props.requestV2Definitions != null &&
        getOptionsForCreate(props.requestV2Definitions, props.runQueryState, props.selectorsById)}
    </Option>
  );
}
